import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  Chip,
  Alert,
  CircularProgress,
  useTheme,
  alpha,
  Fade
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon,
  Newspaper as NewsIcon,
  Balance as BalanceIcon,
  ShowChart as ShowChartIcon,
  Category as CategoryIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
  Line,
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
  Cell,
  ComposedChart
} from 'recharts';

import { macroForecastAPI, macroOutlookAPI } from '../services/api';
import { MacroOutlookSkeleton } from '../components/macro-outlook';

/**
 * Custom Professional Tooltip Components
 */
const ProfessionalTooltip = ({ active, payload, label, title, formatter, labelFormatter }) => {
  const { t } = useTranslation();
  
  if (!active || !payload || !payload.length) return null;

  // Translate metric names using i18n
  const translateMetricName = (name) => {
    return t(`macroOutlook.tooltips.${name}`, name);
  };

  return (
    <Box
      sx={{
        bgcolor: 'rgba(255, 255, 255, 0.98)',
        border: '1px solid #e0e0e0',
        borderRadius: '8px',
        padding: '12px 16px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        minWidth: '200px'
      }}
    >
      {/* Title */}
      {title && (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            color: 'text.secondary',
            textTransform: 'uppercase',
            fontSize: '10px',
            fontWeight: 700,
            letterSpacing: '0.5px',
            mb: 1
          }}
        >
          {title}
        </Typography>
      )}
      
      {/* Label */}
      <Typography
        variant="body2"
        sx={{
          fontWeight: 700,
          color: '#1a1a1a',
          fontSize: '13px',
          mb: 1,
          pb: 1,
          borderBottom: '1px solid #f0f0f0'
        }}
      >
        {labelFormatter ? labelFormatter(label, payload) : label}
      </Typography>
      
      {/* Data Items */}
      {payload.map((entry, index) => (
        <Box
          key={`item-${index}`}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: 0.5,
            gap: 2
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                bgcolor: entry.color,
                flexShrink: 0,
                border: `2px solid ${entry.color}`,
                boxShadow: `0 0 0 2px rgba(255,255,255,0.9), 0 2px 4px rgba(0,0,0,0.2)`,
                filter: 'saturate(1.2) brightness(1.1)'
              }}
            />
            <Typography
              variant="body2"
              sx={{
                fontSize: '12px',
                color: '#666',
                fontWeight: 500
              }}
            >
              {translateMetricName(entry.name)}
            </Typography>
          </Box>
          <Typography
            variant="body2"
            sx={{
              fontSize: '12px',
              fontWeight: 700,
              color: '#1a1a1a'
            }}
          >
            {formatter ? formatter(entry.value, entry.name) : entry.value}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

/**
 * Macro Outlook Dashboard - Professional 4-Section Layout
 * Follows 7-Level Framework visualization
 */
const MacroOutlook = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Data states
  const [regimeData, setRegimeData] = useState(null);
  const [shockData, setShockData] = useState(null);
  const [gatingData, setGatingData] = useState(null);
  const [forecastData, setForecastData] = useState(null);
  const [sectorData, setSectorData] = useState(null);
  const [driverData, setDriverData] = useState(null);
  const [asOfDate, setAsOfDate] = useState(new Date().toISOString().split('T')[0]);

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Fetch from real APIs
      const dashboardResponse = await macroOutlookAPI.getDashboard();
      const dashboard = dashboardResponse.data;
      
      // Set regime data (Level 2)
      if (dashboard.regime) {
        setRegimeData({
          current: dashboard.regime.current,
          history: dashboard.regime.history.map(item => ({
            date: item.as_of_time.split('T')[0],
            score: item.macro_score,
            tailwind_zone: 1,
            headwind_zone: -1
          }))
        });
      }
      
      // Set shock data (Level 3)
      if (dashboard.shock) {
        setShockData({
          daily: dashboard.shock.history,
          cumulative_7d: dashboard.shock.current.cumulative_7d
        });
      }
      
      // Set gating data (Level 4)
      if (dashboard.gating) {
        setGatingData({
          current: dashboard.gating.current,
          history: dashboard.gating.history
        });
      }
      
      // Fetch forecast data (Level 5-6)
      const forecastResponse = await macroForecastAPI.getDashboard();
      const forecastDashboard = forecastResponse.data;
      
      if (forecastDashboard && forecastDashboard.length > 0) {
        const firstTarget = forecastDashboard[0];
        if (firstTarget.forecasts && firstTarget.forecasts.length > 0) {
          const scenarios = processForecasts(firstTarget.forecasts);
          setForecastData({
            targets: forecastDashboard.map(t => t.indicator_id),
            scenarios: scenarios
          });
        }
      }
      
      // Fetch sector data (Level 7)
      const sectorResponse = await macroOutlookAPI.getSectors();
      setSectorData(sectorResponse.data);
      
      // Fetch drivers data (default: target_id=1, horizon='1M')
      const driversResponse = await macroOutlookAPI.getDrivers(1, '1M');
      setDriverData(driversResponse.data);
      
      setAsOfDate(dashboard.as_of_date.split('T')[0]);
      
    } catch (err) {
      console.error('Failed to fetch outlook data:', err);
      setError('Failed to load macro outlook data. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  // Helper to process forecasts into scenario format
  const processForecasts = (forecasts) => {
    const horizons = ['1M', '3M', '6M', '1Y'];
    return horizons.map(horizon => {
      const forecast = forecasts.find(f => f.horizon === horizon);
      if (!forecast || !forecast.scenarios) {
        return null;
      }
      
      const base = forecast.scenarios.find(s => s.scenario === 'BASE');
      const up = forecast.scenarios.find(s => s.scenario === 'UP');
      const down = forecast.scenarios.find(s => s.scenario === 'DOWN');
      
      return {
        horizon,
        baseline: base?.mean_forecast || null,
        upside: up?.mean_forecast || null,
        downside: down?.mean_forecast || null,
        p10: base?.p10 || null,
        p90: base?.p90 || null
      };
    }).filter(item => item !== null && item.baseline !== null);
  };

  // Regime color helper - Professional finance colors
  const getRegimeColor = (regime) => {
    switch (regime) {
      case 'TAILWIND': return '#10b981'; // Professional emerald green (financial growth)
      case 'HEADWIND': return '#dc2626'; // Professional red (financial warning) - deeper, more serious
      case 'NEUTRAL': return '#64748b'; // Professional slate gray (neutral sentiment)
      default: return '#64748b';
    }
  };

  const getRegimeIcon = (regime) => {
    switch (regime) {
      case 'TAILWIND': return <TrendingUpIcon />;
      case 'HEADWIND': return <TrendingDownIcon />;
      case 'NEUTRAL': return <TrendingFlatIcon />;
      default: return <TrendingFlatIcon />;
    }
  };

  if (loading) {
    return <MacroOutlookSkeleton />;
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Fade in timeout={600}>
          <Alert severity="error" sx={{ borderRadius: 2 }}>
            {error}
          </Alert>
        </Fade>
      </Container>
    );
  }

  return (
    <Box 
      sx={{ 
        minHeight: '100vh',
        pb: 6
      }}
    >
      {/* ═══════════════════════════════════════════════════════
          HEADER — Consistent with other pages
          ═══════════════════════════════════════════════════════ */}
      <Container maxWidth="xl" sx={{ pt: 1, pb: 2 }}>
        <Fade in timeout={600}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {/* Left: Icon + Title */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{
                width: 42, height: 42, borderRadius: 1.5,
                background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 4px 14px rgba(59, 130, 246, 0.3)',
              }}>
                <ShowChartIcon sx={{ fontSize: 22, color: '#fff' }} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: '1.35rem', fontWeight: 800, color: '#1e293b', letterSpacing: -0.3 }}>
                  {t('macroOutlook.title', 'Macro Economic Outlook')}
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8', fontWeight: 500 }}>
                  {t('macroOutlook.subtitle', 'Phân tích tổng hợp vĩ mô, tin tức và dự báo thị trường')}
                </Typography>
                <Typography sx={{ fontSize: '0.7rem', color: '#b0b8c4', fontWeight: 400 }}>
                  {t('macroOutlook.asOfDate', 'TÍNH ĐẾN')}: {asOfDate}
                </Typography>
              </Box>
            </Box>

            {/* Right: LIVE chip */}
            <Chip 
              label="LIVE"
              size="small"
              sx={{ 
                bgcolor: alpha(theme.palette.success.main, 0.1),
                color: 'success.main',
                fontWeight: 700,
                fontSize: '0.7rem'
              }}
            />
          </Box>
        </Fade>
      </Container>

      {/* Main Content Container */}
      <Container maxWidth="xl">
        {/* SECTION 1: MACRO REGIME + NEWS SHOCK */}
        <Fade in timeout={800}>
          <Box sx={{ mb: 4 }}>
            <Box display="flex" flexDirection="column" gap={3}>
              <MacroRegimeCard 
                data={regimeData}
                getRegimeColor={getRegimeColor}
                getRegimeIcon={getRegimeIcon}
              />
              <NewsShockCard data={shockData} />
            </Box>
          </Box>
        </Fade>

        {/* SECTION 2: GATING + FORECAST */}
        <Fade in timeout={1000}>
          <Box sx={{ mb: 4 }}>
            <Box display="flex" flexDirection="column" gap={3}>
              <GatingWeightsCard data={gatingData} />
              <ForecastCard data={forecastData} />
            </Box>
          </Box>
        </Fade>

        {/* SECTION 3: DRIVERS */}
        <Fade in timeout={1200}>
          <Box sx={{ mb: 4 }}>
            <DriverContributionCard data={driverData} loading={loading} />
          </Box>
        </Fade>

        {/* SECTION 4: SECTORS */}
        <Fade in timeout={1400}>
          <Box sx={{ mb: 4 }}>
            <SectorHeatmapCard data={sectorData} />
          </Box>
        </Fade>
      </Container>
    </Box>
  );
};

// ============================================================================
// SECTION A: MACRO REGIME CARD
// ============================================================================
const MacroRegimeCard = ({ data, getRegimeColor, getRegimeIcon }) => {
  const { t } = useTranslation();
  
  if (!data) return null;
  
  const { current, history } = data;
  const regimeColor = getRegimeColor(current.regime);

  return (
    <Card 
      sx={{ 
        height: '100%', 
        width: '100%',
        border: '1px solid #e0e0e0',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        borderRadius: '4px'
      }}
    >
      <CardContent sx={{ p: 3 }}>
        {/* Header */}
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography 
            variant="overline" 
            sx={{ 
              color: 'text.secondary',
              fontSize: '10px',
              fontWeight: 700,
              letterSpacing: '1.2px'
            }}
          >
            {t('macroOutlook.regime.title', 'MACRO REGIME')}
          </Typography>
          <Chip
            icon={getRegimeIcon(current.regime)}
            label={current.regime}
            sx={{
              bgcolor: regimeColor,
              color: 'white',
              fontWeight: 700,
              fontSize: '12px',
              height: '28px',
              px: 1.5
            }}
          />
        </Box>

        {/* Current Status - Redesigned Layout */}
        <Box 
          mb={3} 
          p={2.5} 
          sx={{
            bgcolor: `${regimeColor}08`,
            border: `2px solid ${regimeColor}`,
            borderRadius: '4px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          {/* Left: Regime Name */}
          <Box>
            <Typography 
              variant="caption" 
              sx={{ 
                color: 'text.secondary',
                textTransform: 'uppercase',
                fontSize: '10px',
                fontWeight: 600,
                display: 'block',
                mb: 0.5
              }}
            >
              {t('macroOutlook.regime.current', 'TRẠNG THÁI HIỆN TẠI')}
            </Typography>
            <Typography 
              variant="h3" 
              fontWeight="800" 
              sx={{ 
                color: regimeColor,
                letterSpacing: '-1px',
                lineHeight: 1
              }}
            >
              {current.regime}
            </Typography>
          </Box>
          
          {/* Right: Metrics */}
          <Box display="flex" gap={4} alignItems="center">
            <Box textAlign="right">
              <Typography 
                variant="caption" 
                sx={{ 
                  color: 'text.secondary',
                  textTransform: 'uppercase',
                  fontSize: '10px',
                  fontWeight: 600,
                  display: 'block'
                }}
              >
                Score
              </Typography>
              <Typography 
                variant="h4" 
                fontWeight="700" 
                sx={{ 
                  color: 'text.primary',
                  lineHeight: 1.2
                }}
              >
                {current.score.toFixed(3)}
              </Typography>
            </Box>
            
            <Box 
              sx={{ 
                width: '1px', 
                height: '40px', 
                bgcolor: 'divider' 
              }} 
            />
            
            <Box textAlign="right">
              <Typography 
                variant="caption" 
                sx={{ 
                  color: 'text.secondary',
                  textTransform: 'uppercase',
                  fontSize: '10px',
                  fontWeight: 600,
                  display: 'block'
                }}
              >
                Confidence
              </Typography>
              <Typography 
                variant="h4" 
                fontWeight="700" 
                sx={{ 
                  color: 'text.primary',
                  lineHeight: 1.2
                }}
              >
                {(current.confidence * 100).toFixed(0)}%
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Trend Chart */}
        <Box>
          <Typography 
            variant="caption" 
            sx={{ 
              color: '#888',
              textTransform: 'uppercase',
              fontSize: '10px',
              fontWeight: 700,
              display: 'block',
              mb: 1.5
            }}
          >
            Historical Trend (60 Days)
          </Typography>
          <ResponsiveContainer width="100%" height={220}>
            <ComposedChart data={history}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" vertical={false} />
              <XAxis 
                dataKey="date" 
                tick={{ fontSize: 11, fill: '#666' }}
                axisLine={{ stroke: '#e0e0e0' }}
                tickLine={false}
              />
              <YAxis 
                domain={[-1, 1]} 
                tick={{ fontSize: 11, fill: '#666' }}
                axisLine={{ stroke: '#e0e0e0' }}
                tickLine={false}
              />
              <Tooltip 
                wrapperStyle={{ zIndex: 1000 }}
                content={<ProfessionalTooltip 
                  title={t('macroOutlook.regime.title', 'TRẠNG THÁI VĨ MÔ').toUpperCase()}
                  labelFormatter={(label) => {
                    const date = new Date(label);
                    return date.toLocaleDateString('vi-VN', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    });
                  }}
                  formatter={(value, name) => {
                    if (name === 'score') {
                      return value >= 0 ? `+${value.toFixed(3)}` : value.toFixed(3);
                    }
                    return value;
                  }}
                />}
              />
              
              {/* Regime zones */}
              <ReferenceLine 
                y={0.5} 
                stroke="#10b981" 
                strokeDasharray="3 3" 
                strokeWidth={1.5}
                label={{ value: 'Tailwind', fill: '#10b981', fontSize: 11, fontWeight: 600 }} 
              />
              <ReferenceLine 
                y={-0.5} 
                stroke="#dc2626" 
                strokeDasharray="3 3" 
                strokeWidth={1.5}
                label={{ value: 'Headwind', fill: '#dc2626', fontSize: 11, fontWeight: 600 }} 
              />
              
              {/* Background areas */}
              <Area type="monotone" dataKey="tailwind_zone" fill="#10b98110" stroke="none" />
              <Area type="monotone" dataKey="headwind_zone" fill="#dc262610" stroke="none" />
              
              {/* Score line */}
              <Line 
                type="monotone" 
                dataKey="score" 
                stroke={regimeColor} 
                strokeWidth={2.5}
                dot={{ r: 5, fill: regimeColor, strokeWidth: 2, stroke: 'white' }}
                activeDot={{ r: 6 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </Box>
      </CardContent>
    </Card>
  );
};

// ============================================================================
// SECTION B: NEWS SHOCK CARD
// ============================================================================
const NewsShockCard = ({ data }) => {
  const { t } = useTranslation();
  
  if (!data) return null;

  return (
    <Card sx={{ height: '100%', width: '100%' }}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="h6" fontWeight="bold">
            <NewsIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            {t('macroOutlook.shock.title', 'News Shock Impact')}
          </Typography>
          <Chip
            label={`${t('macroOutlook.tooltips.cumulative', 'Cumulative')} 7D: ${data.cumulative_7d > 0 ? '+' : ''}${data.cumulative_7d.toFixed(2)}`}
            color={data.cumulative_7d > 0 ? 'success' : 'error'}
            variant="outlined"
          />
        </Box>

        <Typography variant="body2" color="text.secondary" mb={2}>
          {t('macroOutlook.shock.description', 'Daily news sentiment with time-decay')}
        </Typography>

        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={data.daily}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
            <XAxis dataKey="date" tick={{ fontSize: 12 }} />
            <YAxis tick={{ fontSize: 12 }} />
            <Tooltip 
              wrapperStyle={{ zIndex: 1000 }}
              content={<ProfessionalTooltip 
                title={t('macroOutlook.shock.tooltipTitle', 'TÁC ĐỘNG TIN TỨC').toUpperCase()}
                labelFormatter={(label) => {
                  const date = new Date(label);
                  return date.toLocaleDateString('vi-VN', { 
                    weekday: 'long',
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  });
                }}
                formatter={(value, name) => {
                  if (name === 'shock') {
                    return `${value >= 0 ? '+' : ''}${value.toFixed(3)} điểm`;
                  }
                  if (name === 'decay') {
                    return `${value.toFixed(3)} điểm`;
                  }
                  return value;
                }}
              />}
            />
            <ReferenceLine y={0} stroke="#000" />
            <Bar dataKey="shock" radius={[4, 4, 0, 0]}>
              {data.daily.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.shock >= 0 ? '#10b981' : '#dc2626'} />
              ))}
            </Bar>
            <Line 
              type="monotone" 
              dataKey="decay" 
              stroke="#ff9800" 
              strokeWidth={2}
              strokeDasharray="5 5"
              dot={false}
            />
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};

// ============================================================================
// SECTION C: GATING WEIGHTS CARD
// ============================================================================
const GatingWeightsCard = ({ data }) => {
  const { t } = useTranslation();
  
  if (!data) return null;

  return (
    <Card sx={{ height: '100%', width: '100%' }}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="h6" fontWeight="bold">
            <BalanceIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            {t('macroOutlook.gating.title', 'Signal Weighting')}
          </Typography>
          <Box>
            <Chip 
              label={`${(data.current.w_macro * 100).toFixed(0)}% ${t('macroOutlook.labels.macro', 'Macro')}`}
              sx={{ mr: 1, bgcolor: '#1976d2', color: 'white' }}
              size="small"
            />
            <Chip 
              label={`${(data.current.w_news * 100).toFixed(0)}% ${t('macroOutlook.labels.news', 'News')}`}
              sx={{ bgcolor: '#ff9800', color: 'white' }}
              size="small"
            />
          </Box>
        </Box>

        <Alert severity="info" sx={{ mb: 2 }}>
          {t('macroOutlook.gating.explanation', 'Dynamic weighting: when news shock is strong, the system gives more weight to news signals.')}
        </Alert>

        <ResponsiveContainer width="100%" height={250}>
          <AreaChart data={data.history}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
            <XAxis dataKey="date" tick={{ fontSize: 12 }} />
            <YAxis domain={[0, 1]} tickFormatter={(v) => `${(v * 100).toFixed(0)}%`} tick={{ fontSize: 12 }} />
            <Tooltip 
              wrapperStyle={{ zIndex: 1000 }}
              content={<ProfessionalTooltip 
                title={t('macroOutlook.gating.tooltipTitle', 'TRỌNG SỐ TÍN HIỆU').toUpperCase()}
                labelFormatter={(label) => {
                  const date = new Date(label);
                  return date.toLocaleDateString('vi-VN', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  });
                }}
                formatter={(value, name) => {
                  const percentage = (value * 100).toFixed(1);
                  if (name === 'w_macro') {
                    return `${percentage}% (${t('macroOutlook.labels.macro', 'Vĩ mô')})`;
                  }
                  if (name === 'w_news') {
                    return `${percentage}% (${t('macroOutlook.labels.news', 'Tin tức')})`;
                  }
                  return `${percentage}%`;
                }}
              />}
            />
            <Legend />
            <Area 
              type="monotone" 
              dataKey="w_macro" 
              stackId="1"
              stroke="#1976d2" 
              fill="#1976d2"
              name={t('macroOutlook.tooltips.Macro Weight', 'Macro Weight')}
            />
            <Area 
              type="monotone" 
              dataKey="w_news" 
              stackId="1"
              stroke="#ff9800" 
              fill="#ff9800"
              name={t('macroOutlook.tooltips.News Weight', 'News Weight')}
            />
          </AreaChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};

// ============================================================================
// SECTION D: FORECAST CARD (Fan Chart)
// ============================================================================
const ForecastCard = ({ data }) => {
  const { t } = useTranslation();
  
  if (!data) return null;

  return (
    <Card sx={{ height: '100%', width: '100%' }}>
      <CardContent>
        <Typography variant="h6" fontWeight="bold" mb={2}>
          <ShowChartIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          {t('macroOutlook.forecast.title', 'Forecast Scenarios')}
        </Typography>

        <Box display="flex" gap={1} mb={2}>
          <Chip label={`${t('macroOutlook.tooltips.Baseline', 'Baseline')} (50%)`} color="primary" size="small" />
          <Chip label={`${t('macroOutlook.tooltips.Upside', 'Upside')} (25%)`} color="success" size="small" />
          <Chip label={`${t('macroOutlook.tooltips.Downside', 'Downside')} (25%)`} color="error" size="small" />
        </Box>

        <ResponsiveContainer width="100%" height={280}>
          <ComposedChart data={data.scenarios}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
            <XAxis dataKey="horizon" tick={{ fontSize: 12 }} />
            <YAxis tick={{ fontSize: 12 }} />
            <Tooltip 
              wrapperStyle={{ zIndex: 1000 }}
              content={<ProfessionalTooltip 
                title={t('macroOutlook.forecast.tooltipTitle', 'DỰ BÁO KỊCH BẢN').toUpperCase()}
                labelFormatter={(label) => {
                  const horizonNames = {
                    '1M': t('macroOutlook.labels.1M', '1 tháng'),
                    '3M': t('macroOutlook.labels.3M', '3 tháng'),
                    '6M': t('macroOutlook.labels.6M', '6 tháng'),
                    '1Y': t('macroOutlook.labels.1Y', '1 năm')
                  };
                  return `${t('macroOutlook.labels.horizon', 'Kỳ hạn')}: ${horizonNames[label] || label}`;
                }}
                formatter={(value, name) => {
                  if (!value) return 'N/A';
                  const nameMap = {
                    'baseline': `${t('macroOutlook.tooltips.Baseline', 'Cơ sở')} (50%)`,
                    'upside': `${t('macroOutlook.tooltips.Upside', 'Lạc quan')} (25%)`,
                    'downside': `${t('macroOutlook.tooltips.Downside', 'Bi quan')} (25%)`,
                    'p90': `${t('macroOutlook.tooltips.p90', 'P90')} (10%)`,
                    'p10': `${t('macroOutlook.tooltips.p10', 'P10')} (90%)`
                  };
                  return `${value.toFixed(2)} (${nameMap[name.toLowerCase()] || name})`;
                }}
              />}
            />
            <Legend />
            
            {/* Confidence bands */}
            <Area 
              type="monotone" 
              dataKey="p90" 
              stroke="none" 
              fill="#10b98130"
              name="P90"
            />
            <Area 
              type="monotone" 
              dataKey="p10" 
              stroke="none" 
              fill="#ffffff"
              name="P10"
            />
            
            {/* Scenario lines */}
            <Line 
              type="monotone" 
              dataKey="upside" 
              stroke="#10b981" 
              strokeWidth={2}
              strokeDasharray="5 5"
              name="Upside"
            />
            <Line 
              type="monotone" 
              dataKey="baseline" 
              stroke="#1976d2" 
              strokeWidth={3}
              name="Baseline"
            />
            <Line 
              type="monotone" 
              dataKey="downside" 
              stroke="#dc2626" 
              strokeWidth={2}
              strokeDasharray="5 5"
              name="Downside"
            />
          </ComposedChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};

// ============================================================================
// DRIVER CONTRIBUTION CARD
// ============================================================================
const DriverContributionCard = ({ data, loading }) => {
  const { t } = useTranslation();
  
  if (loading) {
    return (
      <Card sx={{ width: '100%' }}>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }
  
  // Show "no data" only if macro_drivers is truly empty or missing
  if (!data || !data.macro_drivers || data.macro_drivers.length === 0) {
    return (
      <Card sx={{ width: '100%' }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" mb={3}>
            {t('macroOutlook.drivers.title', 'Top Forecast Drivers')}
          </Typography>
          <Alert severity="info">
            {t('macroOutlook.drivers.noData', 'Driver data unavailable. Please generate a forecast first.')}
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ width: '100%' }}>
      <CardContent>
        <Typography variant="h6" fontWeight="bold" mb={3}>
          {t('macroOutlook.drivers.title', 'Top Forecast Drivers')}
        </Typography>

        <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={3}>
          {/* Macro Drivers */}
          <Box flex={1}>
            <Typography variant="subtitle1" fontWeight="bold" mb={2} color="primary">
              {t('macroOutlook.drivers.macro', 'Macro Factors')}
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={data.macro_drivers} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="name" type="category" width={100} />
                <Tooltip 
                  wrapperStyle={{ zIndex: 1000 }}
                  content={<ProfessionalTooltip 
                    title={t('macroOutlook.drivers.tooltipTitle', 'ĐÓNG GÓP YẾU TỐ VĨ MÔ').toUpperCase()}
                    labelFormatter={(label) => {
                      const factorNames = {
                        'Growth': t('macroOutlook.tooltips.Growth', 'Tăng trưởng'),
                        'Inflation': t('macroOutlook.tooltips.Inflation', 'Lạm phát'),
                        'Monetary': t('macroOutlook.tooltips.Monetary', 'Chính sách tiền tệ'),
                        'External': t('macroOutlook.tooltips.External', 'Yếu tố bên ngoài'),
                        'FinCond': t('macroOutlook.tooltips.FinCond', 'Điều kiện tài chính')
                      };
                      return factorNames[label] || label;
                    }}
                    formatter={(value, name) => {
                      if (name === 'contribution') {
                        return `${value >= 0 ? '+' : ''}${(value * 100).toFixed(2)}%`;
                      }
                      return value;
                    }}
                  />}
                />
                <Bar dataKey="contribution" radius={[0, 4, 4, 0]}>
                  {data.macro_drivers.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.contribution >= 0 ? '#10b981' : '#dc2626'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Box>

          {/* News Drivers */}
          <Box flex={1}>
            <Typography variant="subtitle1" fontWeight="bold" mb={2} sx={{ color: '#ff9800' }}>
              {t('macroOutlook.drivers.news', 'Top News Impact')}
            </Typography>
            <Box>
              {data.news_drivers && data.news_drivers.length > 0 ? (
                data.news_drivers.map((item, idx) => (
                  <Box key={idx} mb={2} p={2} bgcolor="#f5f5f5" borderRadius={1}>
                    <Typography variant="body2" fontWeight="bold">
                      {item.title || item.headline || t('macroOutlook.labels.newsItem', 'News item')}
                    </Typography>
                    <Box display="flex" gap={1} mt={1}>
                      <Chip label={`${t('macroOutlook.labels.impact', 'Impact')}: ${item.impact.toFixed(2)}`} size="small" />
                      <Chip label={`${t('macroOutlook.labels.credibility', 'Credibility')}: ${(item.credibility * 100).toFixed(0)}%`} size="small" />
                      <Chip label={item.age || 'N/A'} size="small" color="warning" />
                    </Box>
                  </Box>
                ))
              ) : (
                <Alert severity="info" sx={{ mt: 2 }}>
                  {t('macroOutlook.drivers.noNewsData', 'No significant news impact detected')}
                </Alert>
              )}
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

// ============================================================================
// SECTOR HEATMAP CARD
// ============================================================================
// ============================================================================
// SECTOR HEATMAP CARD - Chart Version
// ============================================================================
const SectorHeatmapCard = ({ data }) => {
  const { t, i18n } = useTranslation();
  
  if (!data) return null;

  // Get factor names from API (dynamic)
  const factors = data.factors || [];
  
  // Transform data for grouped bar chart visualization (dynamic factors)
  const chartData = data.sectors.map(sector => {
    // Get sector name based on language
    const sectorName = i18n.language === 'vi' ? sector.name.vi : sector.name.en;
    const sectorData = {
      name: sectorName.length > 25 ? sectorName.substring(0, 25) + '...' : sectorName,
      fullName: sectorName,
    };
    // Map each factor dynamically
    factors.forEach((factor, idx) => {
      sectorData[factor] = sector.exposures[idx] || 0;
    });
    return sectorData;
  });

  // Sort by total exposure (descending)
  chartData.sort((a, b) => {
    const sumA = factors.reduce((sum, factor) => sum + (a[factor] || 0), 0);
    const sumB = factors.reduce((sum, factor) => sum + (b[factor] || 0), 0);
    return sumB - sumA;
  });

  // Color palette for each factor (dynamic)
  const colorPalette = ['#10b981', '#f59e0b', '#3b82f6', '#8b5cf6', '#ef4444', '#06b6d4', '#f43f5e', '#84cc16'];
  const factorColors = {};
  factors.forEach((factor, idx) => {
    factorColors[factor] = colorPalette[idx % colorPalette.length];
  });

  return (
    <Card sx={{ width: '100%' }}>
      <CardContent>
        <Typography variant="h6" fontWeight="bold" mb={3}>
          <CategoryIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          {t('macroOutlook.sectors.title', 'Bản Đồ Nhiệt Yếu Tố')}
        </Typography>

        <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={3} width="100%">
          {/* Stacked Bar Chart */}
          <Box flex={1} minWidth={0}>
            <Typography variant="subtitle2" mb={2} color="text.secondary">
              {t('macroOutlook.sectors.heatmap', 'Phân bổ độ nhạy cảm theo yếu tố vĩ mô')}
            </Typography>
            
            <ResponsiveContainer width="100%" height={600}>
              <BarChart
                data={chartData}
                layout="vertical"
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" horizontal={true} vertical={false} />
                <XAxis 
                  type="number"
                  domain={[0, 1]}
                  tickFormatter={(value) => `${(value * 100).toFixed(0)}%`}
                  tick={{ fontSize: 11, fill: '#666' }}
                  axisLine={{ stroke: '#e0e0e0' }}
                />
                <YAxis 
                  type="category"
                  dataKey="name"
                  tick={{ fontSize: 11, fill: '#333' }}
                  width={220}
                  axisLine={{ stroke: '#e0e0e0' }}
                />
                <Tooltip
                  wrapperStyle={{ zIndex: 1000 }}
                  content={<ProfessionalTooltip 
                    title={t('macroOutlook.sectors.tooltipTitle', 'ĐỘ NHẠY CẢM NGÀNH').toUpperCase()}
                    labelFormatter={(label, payload) => {
                      if (payload && payload.length > 0) {
                        return payload[0].payload.fullName;
                      }
                      return label;
                    }}
                    formatter={(value, name) => {
                      // Use factor name directly from API (already localized)
                      return `${(value * 100).toFixed(1)}% (${name})`;
                    }}
                  />}
                />
                <Legend 
                  wrapperStyle={{ paddingTop: '20px' }}
                  iconType="circle"
                  formatter={(value) => (
                    <span style={{ fontSize: '12px', fontWeight: 600, color: '#333' }}>
                      {value}
                    </span>
                  )}
                />
                {/* Dynamic bars based on factors from API */}
                {factors.map((factor, idx) => (
                  <Bar 
                    key={factor}
                    dataKey={factor} 
                    stackId="a" 
                    fill={factorColors[factor]} 
                    radius={idx === factors.length - 1 ? [0, 4, 4, 0] : [0, 0, 0, 0]} 
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </Box>

          {/* Ranking Sidebar */}
          <Box sx={{ width: { xs: '100%', md: '280px' }, flexShrink: 0 }}>
            <Box mb={3}>
              <Chip 
                label={t('macroOutlook.sectors.top', 'Top Ngành')} 
                sx={{
                  mb: 2,
                  bgcolor: alpha(factors[0] ? factorColors[factors[0]] : '#10b981', 0.15),
                  color: factors[0] ? factorColors[factors[0]] : '#10b981',
                  fontWeight: 700,
                  fontSize: '12px'
                }}
              />
              {data.topSectors.map((sector, idx) => (
                <Box 
                  key={idx} 
                  display="flex" 
                  alignItems="center" 
                  mb={1.5}
                  p={1.5}
                  sx={{
                    bgcolor: alpha(factors[0] ? factorColors[factors[0]] : '#10b981', 0.05),
                    borderRadius: 1,
                    border: `1px solid ${alpha(factors[0] ? factorColors[factors[0]] : '#10b981', 0.2)}`
                  }}
                >
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      bgcolor: factors[0] ? factorColors[factors[0]] : '#10b981',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '11px',
                      fontWeight: 700,
                      mr: 1.5,
                      flexShrink: 0
                    }}
                  >
                    {idx + 1}
                  </Box>
                  <Typography variant="body2" fontSize="12px" fontWeight={600}>
                    {i18n.language === 'vi' ? sector.vi : sector.en}
                  </Typography>
                </Box>
              ))}
            </Box>
            
            <Box>
              <Chip 
                label={t('macroOutlook.sectors.risk', 'Rủi Ro Cao')} 
                sx={{
                  mb: 2,
                  bgcolor: alpha(factors[4] ? factorColors[factors[4]] : '#ef4444', 0.15),
                  color: factors[4] ? factorColors[factors[4]] : '#ef4444',
                  fontWeight: 700,
                  fontSize: '12px'
                }}
              />
              {data.riskSectors.map((sector, idx) => (
                <Box 
                  key={idx} 
                  display="flex" 
                  alignItems="center" 
                  mb={1.5}
                  p={1.5}
                  sx={{
                    bgcolor: alpha(factors[4] ? factorColors[factors[4]] : '#ef4444', 0.05),
                    borderRadius: 1,
                    border: `1px solid ${alpha(factors[4] ? factorColors[factors[4]] : '#ef4444', 0.2)}`
                  }}
                >
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      bgcolor: factors[4] ? factorColors[factors[4]] : '#ef4444',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '14px',
                      fontWeight: 700,
                      mr: 1.5,
                      flexShrink: 0
                    }}
                  >
                    ✗
                  </Box>
                  <Typography variant="body2" fontSize="12px" fontWeight={600}>
                    {i18n.language === 'vi' ? sector.vi : sector.en}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default MacroOutlook;

